import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import clsx from 'clsx';
import { ComponentProps, ReactElement, ReactNode } from 'react';
import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';
import { InputGroup, InputGroupAddon, InputGroupInput } from '../ui/input-group';

interface FormInputFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  form: UseFormReturn<TFieldValues>;
  name: TName;
  label: ReactNode;
  className?: string;
  inputProps?: ComponentProps<typeof Input> & { 'data-testid'?: string };
  labelTestId?: string;
  addons?: ReactElement<typeof InputGroupAddon> | ReactElement<typeof InputGroupAddon>[];
}

export function FormInputField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  form,
  name,
  label,
  inputProps = {},
  className = '',
  labelTestId,
  addons,
}: FormInputFieldProps<TFieldValues, TName>) {
  const { className: inputClassName, required, ...others } = inputProps;

  const onKeyDownNumberInput = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }
    if (!others.onKeyDown) return;
    others.onKeyDown(event);
  };

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel className="font-bold" data-testid={labelTestId}>
            {label}
            {required && '*'}
          </FormLabel>
          <FormControl>
            <InputGroup>
              {addons}
              <InputGroupInput
                {...field}
                {...others}
                className={clsx('w-2/3', inputClassName)}
                onKeyDown={(event) => {
                  others.type === 'number' ? onKeyDownNumberInput(event) : others?.onKeyDown?.(event);
                }}
              />
            </InputGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
